import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  TextField,
  Typography,
  Stack,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Divider,
  CircularProgress,
  IconButton,
} from "@mui/material";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import BusinessSettingsContext from "../Context/Settings/BusinessSettingsContext";
import {
  getBusinessReplyConfig,
  addDisabledNumber,
  removeDisabledNumber,
} from "../../services/businessReplyConfig.service";

/* ---------------- TYPES ---------------- */

interface BusinessReplyConfig {
  businessId: string;
  disableAIReplies: string[];
}

/* ---------------- COMPONENT ---------------- */

const BusinessReplyConfig: React.FC = () => {
  const { settings } = useContext(BusinessSettingsContext);
  const businessId = settings?.businessId;

  const [config, setConfig] = useState<BusinessReplyConfig | null>(null);
  const [phone, setPhone] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  /* -------- LOAD CONFIG -------- */

  const loadConfig = async () => {
    if (!businessId) return;
    setPageLoading(true);
    const data = await getBusinessReplyConfig(businessId);
    setConfig(data);
    setPageLoading(false);
  };

  useEffect(() => {
    loadConfig();
  }, [businessId]);

  /* -------- ADD NUMBER -------- */

  const handleAdd = async () => {
    if (!phone.trim() || !businessId) return;

    try {
      setLoading(true);
      await addDisabledNumber(businessId, phone);
      setPhone("");
      await loadConfig();
    } finally {
      setLoading(false);
    }
  };

  /* -------- ENTER KEY SUPPORT -------- */

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAdd();
    }
  };

  /* -------- REMOVE NUMBER -------- */

  const handleRemove = async (num: string) => {
    if (!businessId) return;
    await removeDisabledNumber(businessId, num);
    loadConfig();
  };

  /* -------- FILTER -------- */

  const filteredNumbers = useMemo(() => {
    const q = search.replace(/\D/g, "");
    return config?.disableAIReplies?.filter((n) => n.includes(q)) || [];
  }, [config, search]);

  /* ---------------- UI ---------------- */

  if (!businessId) {
    return (
      <Box p={4}>
        <Typography color="text.secondary">
          Please complete Business Settings to manage AI reply controls.
        </Typography>
      </Box>
    );
  }

  if (pageLoading) {
    return (
      <Box p={6} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, width: "100%", minWidth: 0 }}>
      {/* -------- HEADER -------- */}
      <Typography variant="h5" fontWeight={600} mb={0.5}>
        AI reply controls
      </Typography>

      <Typography variant="body2" color="text.secondary" mb={3}>
        Manage customers for whom AI should not reply automatically.
      </Typography>

      <Alert severity="info" sx={{ mb: 4 }}>
        When a number is added here, <strong>AI will stop replying</strong> to
        messages from that customer.
      </Alert>

      {/* -------- ADD SECTION (CENTERED) -------- */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography
          variant="subtitle1"
          fontWeight={500}
          mb={2}
          textAlign="left"
        >
          Disable AI for a customer
        </Typography>

        <Stack
          direction="row"
          spacing={1}
          justifyContent="center"
          alignItems="center"
        >
          <TextField
            label="Customer WhatsApp Number"
            placeholder="e.g. 919004513947"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            onKeyDown={handleKeyDown}
            size="medium"
            sx={{ width: 500 }}
          />

          <IconButton
            color="primary"
            onClick={handleAdd}
            disabled={loading || !phone.trim()}
            sx={{ mt: "4px" }}
          >
            <AddCircleOutlineIcon />
          </IconButton>
        </Stack>
      </Paper>

      {/* -------- LIST SECTION -------- */}
      <Paper sx={{ p: 3 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <TextField
            placeholder="Search number…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ width: 500 }}
          />
          <Typography variant="subtitle1" fontWeight={500}>
            Disabled Numbers ({config?.disableAIReplies?.length || 0})
          </Typography>
        </Stack>

        <Divider sx={{ mb: 2 }} />

        {/* -------- TABLE -------- */}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={80} sx={{ fontWeight: 600 }}>
                Action
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>WhatsApp Number</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredNumbers.length ? (
              filteredNumbers.map((num) => (
                <TableRow key={num} hover>
                  {/* Action FIRST */}
                  <TableCell>
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => handleRemove(num)}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </TableCell>

                  {/* Number SECOND */}
                  <TableCell>{num}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={2} align="center">
                  <Typography variant="body2" color="text.secondary">
                    {search
                      ? "No matching numbers found."
                      : "AI replies are enabled for all customers."}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default BusinessReplyConfig;
